<template>
  <main class="invoice-page">
    <section class="invoice-sheet">
      <header class="invoice-header">
        <div class="brand-block">
          <img :src="logoUrl" class="brand-logo" alt="Profcar logo">
          <div class="brand-copy"><strong>PROFCAR.UZ</strong><span>AVTOMOBIL EHTIYOT QISMLARI</span></div>
        </div>
        <div class="document-block">
          <h1>Sotib olish fakturasi</h1>
          <div class="document-meta"><strong>№ {{ datas.number || datas.id }}</strong><span>от {{ formattedDate }}</span></div>
          <div v-if="printType" class="copy-label">{{ printType }}</div>
        </div>
        <div class="parties-block">
          <div class="party"><span>OLUVCHI:</span><strong>{{ buyerName }}</strong></div>
          <div class="party"><span>SOTUVCHI:</span><strong>{{ sellerName }}</strong></div>
        </div>
      </header>

      <table class="items-table">
        <thead><tr><th class="number-column">№</th><th>Tovar</th><th class="quantity-column">Soni</th><th class="money-column">Narx</th><th class="money-column">Summa</th><th class="currency-column">Valyuta</th></tr></thead>
        <tbody>
          <tr v-for="(item, index) in items" :key="item.id || index">
            <td class="text-center">{{ index + 1 }}</td><td class="product-name">{{ productName(item) }}</td>
            <td class="text-center">{{ formatNumber(item.count) }}</td><td class="text-right">{{ formatNumber(item.kontragent_price) }}</td>
            <td class="text-right">{{ formatNumber(itemTotal(item)) }}</td><td class="text-center currency-name">{{ isDollarItem(item) ? 'Dollar ($)' : "So'm" }}</td>
          </tr>
          <tr v-if="items.length === 0"><td colspan="6" class="empty-row">Mahsulotlar mavjud emas</td></tr>
        </tbody>
      </table>

      <section class="totals-panel">
        <div class="total-item"><div class="currency-icon som-icon">UZS</div><div><span>JAMI SO'M</span><strong>{{ formatNumber(somTotal) }} so'm</strong></div></div>
        <div class="total-item"><div class="currency-icon dollar-icon">$</div><div><span>JAMI DOLLAR</span><strong>$ {{ formatNumber(dollarTotal) }}</strong></div></div>
      </section>
      <div class="product-count"><span class="document-icon">▤</span><strong>Jami mahsulotlar soni: {{ formatNumber(totalCount) }}</strong></div>

      <section class="signatures">
        <div class="signature-row"><strong>Jo'natuvchi:</strong><div class="signature-line"><span>Ism, imzo</span></div></div>
        <div class="signature-row"><strong>Qabul qiluvchi:</strong><div class="signature-line"><span>Ism, imzo</span></div></div>
        <div class="stamp">M.O'.</div>
      </section>
      <footer class="invoice-footer"><span></span></footer>
    </section>
  </main>
</template>

<script>
import axios from 'axios';
import fallbackLogo from '@/assets/chek_logo.jpg';

export default {
  name: 'KirimA4Print',
  data() {
    return { datas: {}, header: { sender: null }, kontragent: null, printType: localStorage.getItem('print_type') || '', fallbackLogo };
  },
  computed: {
    items() { return Array.isArray(this.datas.prixod_table) ? this.datas.prixod_table : []; },
    logoUrl() {
      return this.header && this.header.file_name ? `${axios.defaults.baseURL}api/v1/uploads/${this.header.file_name}` : this.fallbackLogo;
    },
    buyerName() { return (this.header.sender && this.header.sender.firm_name) || (this.datas.sklad && this.datas.sklad.name) || 'PROFCAR.UZ'; },
    sellerName() { return (this.kontragent && this.kontragent.name) || (this.datas.kontragent && this.datas.kontragent.name) || '-'; },
    formattedDate() {
      if (!this.datas.created_at) return '-';
      return new Date(Number(this.datas.created_at) * 1000).toLocaleDateString('ru-RU').replace(/\./g, '-');
    },
    somTotal() { return this.items.reduce((sum, item) => sum + (this.isDollarItem(item) ? 0 : this.itemTotal(item)), 0); },
    dollarTotal() { return this.items.reduce((sum, item) => sum + (this.isDollarItem(item) ? this.itemTotal(item) : 0), 0); },
    totalCount() { return this.items.reduce((sum, item) => sum + Number(item.count || 0), 0); },
  },
  async created() {
    await this.loadInvoice();
    await this.$nextTick();
    setTimeout(() => window.print(), 300);
  },
  mounted() { window.addEventListener('afterprint', this.afterPrint); },
  beforeDestroy() { window.removeEventListener('afterprint', this.afterPrint); },
  methods: {
    async loadInvoice() {
      const invoiceResponse = await axios.get(`api/v1/prixod/id/${this.$route.params.id}`);
      this.datas = invoiceResponse.data || {};
      const requests = [axios.post('api/v1/sklad-settings', { sklad_id: this.datas.sklad_id || localStorage.getItem('sklad_id'), key: 'a4_printer' })];
      if (this.datas.kontragent_id) requests.push(axios.get(`api/v1/kontragent/id/${this.datas.kontragent_id}`));
      const responses = await Promise.allSettled(requests);
      const settings = responses[0].status === 'fulfilled' && responses[0].value.data.data;
      if (settings) { try { this.header = JSON.parse(settings); } catch (e) { this.header = { sender: null }; } }
      if (responses[1] && responses[1].status === 'fulfilled') this.kontragent = responses[1].value.data;
    },
    isDollarItem(item) { return Number(item && item.price_type) === 2; },
    itemTotal(item) {
      const savedTotal = Number(item && item.kontragent_summa);
      return Number.isFinite(savedTotal) ? savedTotal : Number(item.count || 0) * Number(item.kontragent_price || 0);
    },
    productName(item) { return item && item.product ? (item.product.extra_name || item.product.name || '-') : '-'; },
    formatNumber(value) { return Number(value || 0).toLocaleString('ru-RU', { maximumFractionDigits: 3 }); },
    afterPrint() { localStorage.removeItem('print_type'); window.close(); },
  },
};
</script>

<style scoped>
@page { size: A4 portrait; margin: 0; }
.invoice-page { min-height: 100vh; background: #eef3f8; padding: 14px; color: #0b2f63; font-family: Arial, Helvetica, sans-serif; }
.invoice-sheet { position: relative; display: flex; flex-direction: column; box-sizing: border-box; width: 210mm; height: 297mm; margin: 0 auto; padding: 9mm 8mm 20mm; background: #fff; border: 1px solid #d7e3ee; box-shadow: 0 4px 18px rgba(15,54,96,.12); overflow: hidden; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.invoice-header { display: grid; grid-template-columns: 1.05fr 1.25fr 1fr; gap: 7mm; align-items: stretch; margin-bottom: 5mm; }
.brand-block,.document-block,.parties-block { min-height: 34mm; }
.brand-block { display: flex; align-items: center; justify-content: center; gap: 2mm; }
.brand-logo { width: 24mm; height: 28mm; object-fit: contain; }
.brand-copy { display: flex; flex-direction: column; justify-content: center; }
.brand-copy strong { font-size: 17pt; line-height: 1; white-space: nowrap; }
.brand-copy span { margin-top: 2mm; color: #1473b5; font-size: 5.7pt; font-weight: 800; letter-spacing: 0; white-space: nowrap; }
.document-block,.parties-block { background: #edf6fc; border-radius: 3mm; padding: 5mm; }
.document-block { text-align: center; }
.document-block h1 { margin: 0 0 3mm; font-size: 19pt; line-height: 1.15; }
.document-meta { display: flex; justify-content: center; gap: 5mm; font-size: 14pt; }
.copy-label { display: inline-block; min-width: 30mm; margin-top: 3mm; padding: 1mm 5mm; background: #d6eaf8; border-radius: 4mm; }
.parties-block { display: flex; flex-direction: column; justify-content: center; gap: 5mm; }
.party span { display: block; margin-bottom: 1.5mm; font-size: 10pt; font-weight: 800; }
.party strong { color: #17243a; font-size: 11pt; text-transform: uppercase; }
.items-table { width: 100%; border-collapse: separate; border-spacing: 0; table-layout: fixed; font-size: 9.5pt; }
.items-table th { padding: 3mm 2mm; color: #fff; background: #17639c; border-right: 1.5px solid #b9dcf2; border-bottom: 1.5px solid #0b456f; font-size: 10.5pt; }
.items-table th:first-child { border-radius: 2mm 0 0; }.items-table th:last-child { border-radius: 0 2mm 0 0; border-right: 0; }
.items-table td { padding: 1.7mm 2mm; color: #18385f; border-right: 1.2px solid #8ebcd8; border-bottom: 1.2px solid #aacde1; vertical-align: middle; }
.items-table td:first-child { border-left: 1px solid #c8e1ef; }.items-table tbody tr:nth-child(even) td { background: #edf7fc; }.items-table tbody tr:nth-child(odd) td { background: #fff; }
.number-column { width: 10mm; }.quantity-column { width: 16mm; }.money-column { width: 22mm; }.currency-column { width: 27mm; }
.product-name { overflow-wrap: anywhere; }.currency-name { font-weight: 700; white-space: nowrap; }.text-center { text-align: center; }.text-right { text-align: right; }.empty-row { text-align: center; padding: 8mm !important; }
.totals-panel { display: grid; grid-template-columns: 1fr 1fr; margin-top: 5mm; background: #edf6fc; border-radius: 3mm; overflow: hidden; }
.total-item { display: flex; align-items: center; gap: 5mm; min-height: 25mm; padding: 4mm 6mm; }.total-item + .total-item { border-left: 1px solid #7fb5d8; }
.total-item span { display: block; margin-bottom: 1mm; font-size: 10pt; font-weight: 800; }.total-item strong { display: block; font-size: 18pt; line-height: 1; }
.currency-icon { display: flex; align-items: center; justify-content: center; width: 16mm; height: 16mm; flex: 0 0 16mm; border-radius: 50%; color: #fff; font-weight: 900; }
.som-icon { background: #17639c; font-size: 7pt; }.dollar-icon { background: #0a9f4a; font-size: 22pt; }
.product-count { display: flex; align-items: center; gap: 4mm; margin-top: 2mm; padding: 3mm 5mm; background: #edf6fc; border-radius: 2mm; font-size: 11pt; }.document-icon { font-size: 18pt; line-height: 1; }
.signatures { position: relative; margin-top: auto; padding: 7mm 2mm 0; color: #153d6e; }.signature-row { display: grid; grid-template-columns: 34mm 1fr; align-items: end; gap: 4mm; margin-bottom: 9mm; font-size: 11pt; }
.signature-line { position: relative; height: 6mm; border-bottom: 1px solid #153d6e; }.signature-line span { position: absolute; top: 7mm; left: 50%; transform: translateX(-50%); font-size: 8pt; font-style: italic; }.stamp { position: absolute; right: 1mm; bottom: -2mm; font-weight: 800; font-size: 12pt; }
.invoice-footer { position: absolute; right: 0; bottom: 0; left: 0; height: 15mm; background: #0b4f82; clip-path: polygon(0 45%,25% 78%,67% 95%,100% 58%,100% 100%,0 100%); }.invoice-footer span { position: absolute; inset: 0; background: #7dc8f2; clip-path: polygon(0 28%,25% 62%,67% 78%,100% 42%,100% 52%,67% 90%,25% 74%,0 40%); }
@media print { .invoice-page { width: 210mm; height: 297mm; min-height: 0; padding: 0; background: #fff; }.invoice-sheet { margin: 0; border: 0; box-shadow: none; page-break-after: avoid; } }
</style>

<style>
@media print {
  html, body { width: 210mm !important; height: 297mm !important; margin: 0 !important; padding: 0 !important; overflow: hidden !important; }
  body > * { visibility: hidden !important; }
  .invoice-page, .invoice-page * { visibility: visible !important; }
  .invoice-page { position: absolute !important; inset: 0 !important; }
}
</style>
